import { UTCDate } from '@date-fns/utc';
import { CalendarIcon } from '@heroicons/react/20/solid';
import { ArrowUpCircleIcon } from '@heroicons/react/24/solid';
import { endOfDay, format, startOfDay, subMonths } from 'date-fns';
import Link from 'next/link';
import { useMemo } from 'react';
import { useGetApiBackOfficeUsersCurrentUserDetail } from '@/api/backoffice/back-office-users/back-office-users';
import { useGetApiPickemsCurrentPlayerHistory } from '@/api/backoffice/pickems/pickems';
import { GameIcon } from '@/components/GameIcon';
import type { SupportedGame } from '@/config/supportedGames';
import { mapGameFilterToVideoGameName } from '@/utils/gameMappers';
import {
  ErrorAuthNeededForHistory,
  ErrorLoadingPickemHistory,
  getColorsByPercentage,
  HistoryLoading,
  NoPickemsHistory,
} from './PickemsBetHistoryCommon';

const PickemsBetHistory = ({ game }: { game: SupportedGame }) => {
  const fromDate = useMemo(() => {
    const today = startOfDay(new UTCDate());
    return subMonths(today, 1).toISOString();
  }, []);

  const toDate = useMemo(() => {
    return endOfDay(new UTCDate()).toISOString();
  }, []);

  const {
    data,
    isLoading: isLoadingHistory,
    error: errorHistory,
  } = useGetApiPickemsCurrentPlayerHistory({
    From: fromDate,
    To: toDate,
    IncludeCancelledBets: true,
    PageNumber: 1,
    PageSize: 100,
    VideoGame: mapGameFilterToVideoGameName(game),
  });

  const { data: account, isLoading: isLoadingAccount } =
    useGetApiBackOfficeUsersCurrentUserDetail();

  const isLoading = isLoadingHistory || isLoadingAccount;

  if (isLoading) {
    return <HistoryLoading />;
  }

  if (errorHistory) {
    return <ErrorLoadingPickemHistory />;
  }

  if (!(isLoading || account)) {
    return <ErrorAuthNeededForHistory />;
  }

  if (data && data.data.length === 0) {
    return <NoPickemsHistory />;
  }

  return (
    <div className="flex flex-col gap-4">
      {data?.data.map(({ date, pickems }) => {
        const correctPicks =
          pickems?.filter((pickem) => pickem.win === true).length ?? 0;
        const totalPicks = pickems?.length ?? 0;

        // Skip days with no pickems
        if (totalPicks === 0) {
          return null;
        }

        const successPercentage = (correctPicks / totalPicks) * 100;
        const color = getColorsByPercentage(successPercentage);

        return (
          <Link
            className="block transition-colors hover:bg-[#0F2132]"
            href={`/${game}/pickems/${format(date || '', 'yyyy-MM-dd')}`}
            key={date}
          >
            <div className="flex w-full flex-col gap-4 rounded-lg border border-[#1B2B3A] bg-[#0A1621] p-4">
              <div className="flex w-full items-center justify-between">
                <GameIcon className="size-5" game={game} />
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  <span className="text-sm">
                    {format(date || '', 'd. MMM, yyyy')}
                  </span>
                </div>
              </div>

              <div className="flex w-full items-center justify-center text-gray-400">
                Match Day
              </div>

              <div className="flex items-center justify-center gap-2">
                <div>
                  <ArrowUpCircleIcon
                    className="h-4 w-4"
                    color={color}
                    title="Success Rate"
                  />
                </div>
                <span style={{ color }}>
                  {correctPicks}/{totalPicks} Correct
                </span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default PickemsBetHistory;
